/**
 * ShareCard — vertical infographic for sharing Oracle correlations.
 *
 * Three faces:
 *   - <ShareCardFront />   — comparison hook (medallion + two traditions)
 *   - <ShareCardBack />    — five grabby truths + QR (works for both card types)
 *   - <ShareCardInsight /> — single-subject deep-dive (one tradition/concept,
 *                            medallion = "INSIGHT", deeper-resonance bullets,
 *                            QR). Used when the Oracle response explores a
 *                            single concept rather than a correlation.
 *
 * All render at native 1080×N pixel dimensions inside a hidden, off-screen
 * container that html-to-image captures. Visual design is locked to the
 * approved v3 mockup; brand "C" glyph is the SAME spec as Navigation.jsx.
 */
import React from "react";

// ---------- Shared atoms ----------

export function BrandGlyph({ size = 68 }) {
  // EXACT spec from Navigation.jsx Glyph(): Cormorant Garamond italic medium,
  // gold on white, rounded square, 6px right pad for optical centering.
  return (
    <span
      aria-label="Cor·relate"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        background: "#FFFFFF",
        border: "1px solid hsl(35 15% 85%)",
        borderRadius: Math.round(size * 0.18),
        boxShadow: "0 1px 0 rgba(0,0,0,.04), 0 4px 10px rgba(120,80,30,.18)",
        fontFamily: '"Cormorant Garamond", serif',
        fontStyle: "italic",
        fontWeight: 500,
        color: "hsl(38 60% 48%)",
        fontSize: Math.round(size * 0.88),
        lineHeight: 1,
        paddingRight: Math.round(size * 0.09),
        userSelect: "none",
      }}
    >
      C
    </span>
  );
}

function Masthead({ handle, number = "001", smallC = false, hideHandle = false }) {
  return (
    <div style={{
      position: "absolute", top: 0, left: 0, right: 0, padding: "44px 64px 0",
      display: "flex", alignItems: "flex-start", justifyContent: "space-between",
      zIndex: 6,
    }}>
      <div style={mono(20, "rgba(70,45,20,.75)", { width: 180, paddingTop: 36 })}>
        {hideHandle ? "" : (handle ? `@${handle}` : "")}
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <BrandGlyph size={smallC ? 104 : 136} />
        <div style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontWeight: 500,
          color: "hsl(38 60% 48%)",
          fontSize: 36,
          marginTop: 16,
          letterSpacing: "0.02em",
          textShadow: "0 1px 0 rgba(255,255,255,.5)",
        }}>
          Cor<span style={{ opacity: 0.7, margin: "0 6px" }}>·</span>relate
        </div>
        {!smallC && (
          <div style={mono(17, "hsl(30 12% 45%)", { marginTop: 12, letterSpacing: ".28em" })}>
            THE SAME TRUTH, IN EVERY TONGUE
          </div>
        )}
      </div>
      <div style={mono(20, "rgba(70,45,20,.75)", { width: 180, paddingTop: 36, textAlign: "right" })}>
        № {number}
      </div>
    </div>
  );
}

function Footer() {
  return (
    <div style={{
      position: "absolute", bottom: 0, left: 0, right: 0, padding: "44px 56px",
      display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 6,
    }}>
      <span style={{
        fontFamily: '"Cinzel", serif', fontWeight: 600, letterSpacing: ".28em",
        color: "#f0d99a", fontSize: 22,
      }}>
        CORRELATE.ONLINE
      </span>
      <span style={{
        position: "relative", padding: "18px 32px",
        border: "2px solid #d9a64a", color: "#fff3cf",
        fontFamily: '"Cinzel", serif', fontWeight: 600,
        letterSpacing: ".3em", fontSize: 20,
        background: "rgba(20,10,4,.5)",
      }}>
        ASKED OF THE ORACLE
        <span style={diamondCap("left")} />
        <span style={diamondCap("right")} />
      </span>
    </div>
  );
}

const diamondCap = (side) => ({
  content: "",
  position: "absolute",
  top: "50%",
  [side]: -14,
  width: 24, height: 24,
  transform: "translateY(-50%) rotate(45deg)",
  border: "2px solid #d9a64a",
  background: "rgba(20,10,4,.5)",
  display: "block",
});

const mono = (size, color, extra = {}) => ({
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: size,
  letterSpacing: ".22em",
  textTransform: "uppercase",
  color,
  ...extra,
});

const Parchment = ({ height = "50%", top = true }) => (
  <div style={{
    position: "absolute", left: 0, right: 0,
    [top ? "top" : "bottom"]: 0, height,
    background: top
      ? "radial-gradient(ellipse at 18% 20%, rgba(238,200,140,0.45), transparent 55%), radial-gradient(ellipse at 80% 8%, rgba(245,220,165,0.55), transparent 55%), linear-gradient(180deg, #f6e9cd 0%, #efdfbb 100%)"
      : "radial-gradient(ellipse at 80% 100%, rgba(64,38,18,0.55), transparent 55%), radial-gradient(ellipse at 15% 70%, rgba(140,90,40,0.45), transparent 55%), linear-gradient(180deg, #8a5a2c 0%, #5a3818 60%, #2d1a0a 100%)",
  }} />
);

const Grain = () => (
  <div style={{
    position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.5, mixBlendMode: "multiply",
    backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.2  0 0 0 0 0.15  0 0 0 0 0.08  0 0 0 0.08 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
  }} />
);

// Faint astrological wheel watermark
const Wheel = ({ position = "top", color = "#6a4514", opacity = 0.13 }) => (
  <svg
    viewBox="0 0 200 200"
    fill="none" stroke={color} strokeWidth="0.5"
    style={{
      position: "absolute", left: "50%", transform: "translateX(-50%)",
      width: 1360, height: 1360, opacity, pointerEvents: "none",
      [position]: -400,
    }}
  >
    <circle cx="100" cy="100" r="90" />
    <circle cx="100" cy="100" r="78" />
    <circle cx="100" cy="100" r="60" />
    <line x1="100" y1="10" x2="100" y2="190" />
    <line x1="10" y1="100" x2="190" y2="100" />
    <line x1="35" y1="35" x2="165" y2="165" />
    <line x1="165" y1="35" x2="35" y2="165" />
  </svg>
);

// ---------- Medallion (contrast-boosted) ----------
function Medallion({ truth, size = 420, labelFontSize = 19, truthFontSize = 36 }) {
  return (
    <div style={{
      position: "relative",
      width: size, height: size, borderRadius: "50%",
      background:
        "radial-gradient(circle at 32% 28%, #f6dba0 0%, #c89947 28%, #6e4012 68%, #2a1605 100%)",
      boxShadow: [
        "0 0 0 4px rgba(120,80,30,.6)",
        "0 0 0 16px rgba(245,233,207,.96)",
        "0 0 0 20px rgba(120,80,30,.55)",
        "0 0 0 24px rgba(245,233,207,.55)",
        "0 0 80px rgba(255,200,120,.4)",
        "0 36px 80px rgba(40,20,5,.55)",
        "inset 0 -28px 60px rgba(40,20,5,.62)",
        "inset 0 28px 60px rgba(255,235,180,.45)",
      ].join(", "),
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {/* Sacred geometry overlay */}
      <div style={{
        position: "absolute", inset: 28, borderRadius: "50%",
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200' fill='none' stroke='%23f5e0a8' stroke-width='0.6' opacity='0.5'><circle cx='100' cy='100' r='80'/><circle cx='100' cy='100' r='60'/><polygon points='100,30 161,135 39,135'/><polygon points='100,170 161,65 39,65'/></svg>\")",
        backgroundSize: "cover",
      }} />
      {/* Inner dark vignette to lift text contrast */}
      <div style={{
        position: "absolute", inset: 36, borderRadius: "50%",
        background: "radial-gradient(circle at 50% 50%, rgba(20,10,4,0.55) 0%, rgba(20,10,4,0.32) 50%, transparent 75%)",
      }} />
      <div style={{
        position: "relative", zIndex: 2, textAlign: "center", padding: `0 ${Math.round(size * 0.09)}px`,
        color: "#fff7df",
        textShadow:
          "0 2px 0 rgba(20,10,4,.95), 0 0 24px rgba(20,10,4,.8), 0 0 4px rgba(20,10,4,.95)",
      }}>
        <div style={{
          fontFamily: '"Cinzel", serif', fontWeight: 700, fontSize: labelFontSize,
          letterSpacing: ".36em", color: "#ffe28a", marginBottom: Math.round(labelFontSize * 0.95),
          textShadow: "0 1px 0 rgba(20,10,4,.9)",
        }}>
          — COR<span style={{ color: "#fff8e3", margin: "0 4px" }}>•</span>RELATION —
        </div>
        <div style={{
          fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic",
          fontWeight: 600, fontSize: truthFontSize, lineHeight: 1.18, color: "#fff8e3",
        }}>
          {`\u201C${truth}\u201D`}
        </div>
      </div>
    </div>
  );
}

const Flame = ({ side, topPct = "48.5%", size = 56 }) => (
  <svg
    viewBox="0 0 24 24" fill="#d9a64a"
    style={{
      position: "absolute", width: size, height: size,
      filter: "drop-shadow(0 0 14px rgba(255,180,80,.55))",
      zIndex: 6, top: topPct,
      [side]: 68,
    }}
  >
    <path d="M12 2c-1 3-4 5-4 9a4 4 0 0 0 8 0c0-2-1-3-1-5 2 1 3 3 3 6a6 6 0 0 1-12 0c0-5 4-7 6-10z" />
  </svg>
);

// ---------- Tradition halves ----------
function TraditionHalf({ side, glyph, tagText, name, essence, topPct, heightPct, sizes }) {
  const isTop = side === "top";
  const s = sizes || { name: 88, essence: 34, tag: 22, glyphBox: 56, ornament: 84 };
  return (
    <div style={{
      position: "absolute", left: 0, right: 0,
      top: topPct, height: heightPct,
      padding: "0 80px",
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", textAlign: "center", zIndex: 3,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginBottom: 6 }}>
        <span style={{
          height: 1, width: s.ornament,
          background: `linear-gradient(90deg, transparent, ${isTop ? "#a87528" : "#f0c970"}, transparent)`,
        }} />
        <div style={{
          width: s.glyphBox, height: s.glyphBox, color: isTop ? "#a87528" : "#f0c970",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {glyph}
        </div>
        <span style={{
          height: 1, width: s.ornament,
          background: `linear-gradient(90deg, transparent, ${isTop ? "#a87528" : "#f0c970"}, transparent)`,
        }} />
      </div>
      <div style={{
        fontFamily: '"Cinzel", serif', fontWeight: 600, letterSpacing: ".34em",
        color: isTop ? "#6a4514" : "#f0c970", fontSize: s.tag,
        margin: `${Math.round(s.tag * 0.4)}px 0 ${Math.round(s.tag * 0.9)}px`,
      }}>
        {tagText}
      </div>
      <h2 style={{
        fontFamily: '"Cormorant Garamond", serif', fontWeight: 500,
        fontSize: s.name, lineHeight: 1, margin: `0 0 ${Math.round(s.name * 0.22)}px`,
        color: isTop ? "#2d1a0a" : "#fdf0d2",
        letterSpacing: "-0.01em",
        textShadow: isTop ? "none" : "0 2px 0 rgba(0,0,0,.35)",
      }}>
        {name}
      </h2>
      <p style={{
        fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic",
        fontWeight: 400, fontSize: s.essence, lineHeight: 1.3, margin: 0, maxWidth: "92%",
        color: isTop ? "#4a3220" : "#f6e3bc",
      }}>
        {essence}
      </p>
    </div>
  );
}

// ---------- FRONT CARD ----------
export function ShareCardFront({
  width = 1080,
  height = 1920,
  handle = "",
  number = "001",
  traditionA = { name: "—", tag: "Tradition A", essence: "", glyph: null },
  traditionB = { name: "—", tag: "Tradition B", essence: "", glyph: null },
  sharedTruth = "",
}) {
  // Layout adapts to aspect ratio. The two supported targets are 9:16 (Story/
  // Reels) and 4:5 (IG Feed). For tall (9:16) we keep generous typography; for
  // shorter (4:5) we shrink the medallion and the names so nothing overlaps.
  const ratio = height / width;
  const tall = ratio > 1.55; // 9:16 ≈ 1.78, 4:5 = 1.25
  const sizes = tall
    ? { name: 88, essence: 34, tag: 22, glyphBox: 56, ornament: 84 }
    : { name: 64, essence: 26, tag: 18, glyphBox: 46, ornament: 64 };
  const medallion = tall
    ? { size: 420, label: 19, truth: 36 }
    : { size: 300, label: 15, truth: 27 };
  const zones = tall
    ? { topY: "16%", topH: "22%", medY: "38%", medH: "24%", botY: "62%", botH: "22%", flameY: "48.5%" }
    : { topY: "13%", topH: "26%", medY: "39%", medH: "22%", botY: "61%", botH: "26%", flameY: "47%" };
  const flameSize = tall ? 56 : 42;

  return (
    <div
      data-share-card="front"
      style={{
        position: "relative", width, height, color: "#2a1a0c", overflow: "hidden",
        fontFamily: '"Cormorant Garamond", serif',
        background: "#f6e9cd",
      }}
    >
      <Parchment top height="50%" />
      <Parchment top={false} height="50%" />
      <Grain />
      <Wheel position="top" color="#6a4514" opacity={0.13} />
      <Wheel position="bottom" color="#f5e9cf" opacity={0.07} />

      <Masthead handle={handle} number={number} smallC={!tall} />

      <TraditionHalf
        side="top"
        glyph={traditionA.glyph}
        tagText={traditionA.tag}
        name={traditionA.name}
        essence={traditionA.essence}
        topPct={zones.topY}
        heightPct={zones.topH}
        sizes={sizes}
      />

      <div style={{
        position: "absolute", top: zones.medY, left: 0, right: 0, height: zones.medH,
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 7,
      }}>
        <Medallion
          truth={sharedTruth}
          size={medallion.size}
          labelFontSize={medallion.label}
          truthFontSize={medallion.truth}
        />
      </div>
      <Flame side="left" topPct={zones.flameY} size={flameSize} />
      <Flame side="right" topPct={zones.flameY} size={flameSize} />

      <TraditionHalf
        side="bottom"
        glyph={traditionB.glyph}
        tagText={traditionB.tag}
        name={traditionB.name}
        essence={traditionB.essence}
        topPct={zones.botY}
        heightPct={zones.botH}
        sizes={sizes}
      />

      <Footer />
    </div>
  );
}

// ---------- BACK CARD ----------
export function ShareCardBack({
  width = 1080,
  height = 1920,
  handle = "",
  number = "001",
  question = "",
  truths = [],
  qrDataUrl = null,
  shortUrl = "correlate.online",
}) {
  // 4:5 is shorter — shrink the question banner, truth text, and QR plate
  // so all 5 truths + QR still fit comfortably above the footer.
  const tall = (height / width) > 1.55;
  const t = tall
    ? { qFont: 34, qPad: "20px 32px", qMb: 36, headFont: 22, headMb: 32, truthFont: 38, truthGap: 32, arrow: 34, qrTop: 44, qrImg: 220, qrTitle: 24, qrUrl: 16 }
    : { qFont: 26, qPad: "14px 24px", qMb: 22, headFont: 17, headMb: 20, truthFont: 26, truthGap: 18, arrow: 24, qrTop: 24, qrImg: 150, qrTitle: 17, qrUrl: 12 };
  return (
    <div
      data-share-card="back"
      style={{
        position: "relative", width, height, color: "#2a1a0c", overflow: "hidden",
        fontFamily: '"Cormorant Garamond", serif',
      }}
    >
      <Parchment top height="18%" />
      <Parchment top={false} height="82%" />
      <Grain />
      <Wheel position="top" color="#6a4514" opacity={0.13} />

      <Masthead handle={handle} number={number} smallC />

      <div style={{
        position: "absolute", top: tall ? 180 : 150, right: 48,
        ...mono(20, "#a87528"),
      }}>
        2 / 2
      </div>

      <div style={{
        position: "absolute", top: "18%", left: 0, right: 0, bottom: 128,
        padding: "44px 64px 0",
        color: "#fef0cc", zIndex: 5,
        display: "flex", flexDirection: "column",
      }}>
        {question && (
          <div style={{
            fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontWeight: 500,
            fontSize: t.qFont, lineHeight: 1.32, color: "#fef0cc",
            textAlign: "center", padding: t.qPad, margin: `0 0 ${t.qMb}px`,
            borderTop: "1px solid rgba(217,166,74,.5)",
            borderBottom: "1px solid rgba(217,166,74,.5)",
          }}>
            {`\u201C${question}\u201D`}
          </div>
        )}

        <div style={{
          fontFamily: '"Cinzel", serif', fontWeight: 700, fontSize: t.headFont,
          letterSpacing: ".36em", color: "#f0c970", textAlign: "center",
          margin: `0 0 ${t.headMb}px`,
        }}>
          — THE CORRELATION DEEPENS —
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: t.truthGap }}>
          {truths.map((tr, i) => (
            <div key={`truth-${i}-${String(tr).slice(0,24)}`} style={{
              display: "flex", gap: 22, alignItems: "flex-start",
              fontFamily: '"Cormorant Garamond", serif', fontSize: t.truthFont,
              fontWeight: 500,
              lineHeight: 1.32, color: "#fff4d0",
              textShadow: "0 1px 2px rgba(0,0,0,.55)",
            }}>
              <svg
                viewBox="0 0 24 24" fill="none" stroke="#d9a64a" strokeWidth="2.4"
                style={{
                  flexShrink: 0, marginTop: Math.round(t.truthFont * 0.31), width: t.arrow, height: t.arrow,
                  filter: "drop-shadow(0 0 8px rgba(255,190,90,.45))",
                }}
              >
                <path d="M2 12 L20 12 M14 6 L20 12 L14 18" />
              </svg>
              {/* Render lightweight markdown: **bold** and *italic* — returns React nodes, no HTML injection. */}
              <span>{renderInline(tr)}</span>
            </div>
          ))}
        </div>

        {qrDataUrl && (
          <div style={{
            margin: `${t.qrTop}px auto 0`,
            background: "#f6e9cd",
            border: "3px solid #a87528",
            boxShadow: "0 16px 40px rgba(0,0,0,.45), inset 0 0 0 6px #f6e9cd, inset 0 0 0 7px rgba(120,80,30,.4)",
            padding: tall ? "24px 32px" : "16px 20px",
            display: "flex", alignItems: "center", gap: tall ? 30 : 20,
            maxWidth: "84%",
          }}>
            <img src={qrDataUrl} alt="QR" style={{ width: t.qrImg, height: t.qrImg, display: "block", background: "#fff" }} />
            <div style={{ flex: 1 }}>
              <div style={{
                fontFamily: '"Cinzel", serif', fontWeight: 700, fontSize: t.qrTitle,
                letterSpacing: ".28em", color: "#6a4514", lineHeight: 1.35,
              }}>
                SCAN<br />TO READ<br />THE FULL<br />CONVERGENCE
              </div>
              <div style={{
                marginTop: 14,
                fontFamily: '"JetBrains Mono", monospace', fontSize: t.qrUrl,
                letterSpacing: ".12em", color: "#6a4514", textTransform: "uppercase",
              }}>
                {shortUrl}
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

// ---------- INSIGHT FRONT CARD ----------
// For single-subject Oracle prompts (deep-dives, "explain X", "what is Y?")
// where there's no second tradition to correlate against. Same parchment +
// gold aesthetic, but composed as a single illuminated page: big tradition
// glyph at the top, name + essence below it, medallion in the middle with the
// key insight, then the same 5 grabby truths + QR below. Reads like a feature
// page in an esoteric encyclopedia.
export function ShareCardInsight({
  width = 1080,
  height = 1920,
  handle = "",
  number = "002",
  subject = { name: "—", tag: "Tradition", essence: "", glyph: null, slug: null },
  keyInsight = "",
  truths = [],
  qrDataUrl = null,
  shortUrl = "correlate.online",
}) {
  const tall = (height / width) > 1.55;
  const sizes = tall
    ? { glyphBig: 144, name: 96, essence: 30, tag: 24, label: 19, truth: 28, truthGap: 26, arrow: 30, medSize: 360, medLabel: 17, medTruth: 30, qrImg: 180, qrTitle: 19, qrUrl: 14 }
    : { glyphBig: 112, name: 72, essence: 24, tag: 19, label: 16, truth: 23, truthGap: 20, arrow: 26, medSize: 270, medLabel: 14, medTruth: 24, qrImg: 140, qrTitle: 16, qrUrl: 12 };

  return (
    <div
      data-share-card="insight"
      style={{
        position: "relative", width, height, color: "#2a1a0c", overflow: "hidden",
        fontFamily: '"Cormorant Garamond", serif',
        background: "#f6e9cd",
      }}
    >
      {/* Single warm parchment background — no honey bottom split */}
      <div style={{
        position: "absolute", inset: 0,
        background:
          "radial-gradient(ellipse at 50% 5%,  rgba(245,220,165,0.65), transparent 55%), " +
          "radial-gradient(ellipse at 15% 30%, rgba(238,200,140,0.45), transparent 55%), " +
          "radial-gradient(ellipse at 85% 70%, rgba(220,180,110,0.55), transparent 55%), " +
          "radial-gradient(ellipse at 50% 100%, rgba(140,90,40,0.45), transparent 65%), " +
          "linear-gradient(180deg, #f6e9cd 0%, #efdfbb 50%, #d8b87a 100%)",
      }} />
      <Grain />
      {/* Large astrological wheel watermark — single, centered, faint */}
      <svg viewBox="0 0 200 200" fill="none" stroke="#6a4514" strokeWidth="0.4"
        style={{
          position: "absolute", left: "50%", top: "50%",
          transform: "translate(-50%, -50%)",
          width: 1300, height: 1300, opacity: 0.1, pointerEvents: "none",
        }}
      >
        <circle cx="100" cy="100" r="92" />
        <circle cx="100" cy="100" r="80" />
        <circle cx="100" cy="100" r="65" />
        <circle cx="100" cy="100" r="48" />
        <line x1="100" y1="8" x2="100" y2="192" />
        <line x1="8" y1="100" x2="192" y2="100" />
        <line x1="35" y1="35" x2="165" y2="165" />
        <line x1="165" y1="35" x2="35" y2="165" />
        <line x1="55" y1="13" x2="145" y2="187" />
        <line x1="145" y1="13" x2="55" y2="187" />
        <line x1="13" y1="55" x2="187" y2="145" />
        <line x1="187" y1="55" x2="13" y2="145" />
      </svg>

      <Masthead handle={handle} number={number} smallC={!tall} hideHandle />

      {/* QR — compact top-left corner stamp, out of the way of the bullet text.
          @handle attribution sits under the stamp for resharing credit. */}
      {qrDataUrl && (
        <div style={{
          position: "absolute",
          top: tall ? 36 : 28,
          left: tall ? 36 : 28,
          zIndex: 8,
          display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8,
        }}>
          <div style={{
            background: "#fff8e3",
            border: "2px solid #a87528",
            boxShadow: "0 6px 18px rgba(40,20,5,.35), inset 0 0 0 4px #fff8e3, inset 0 0 0 5px rgba(120,80,30,.35)",
            padding: 8,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <img
              src={qrDataUrl}
              alt="QR"
              style={{ width: tall ? 130 : 100, height: tall ? 130 : 100, display: "block", background: "#fff" }}
            />
          </div>
          <div style={{
            fontFamily: '"Cinzel", serif', fontWeight: 700,
            fontSize: tall ? 10 : 8.5, letterSpacing: ".28em",
            color: "#6a4514", textAlign: "center",
            width: (tall ? 130 : 100) + 16,
          }}>
            SCAN&nbsp;FOR&nbsp;FULL
          </div>
          {handle && (
            <div style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: tall ? 10 : 8, letterSpacing: ".18em",
              color: "rgba(70,45,20,.75)", textAlign: "center",
              width: (tall ? 130 : 100) + 16,
            }}>
              @{handle}
            </div>
          )}
        </div>
      )}

      {/* SUBJECT BLOCK — tradition glyph + name + essence */}
      <div style={{
        position: "absolute", left: 0, right: 0,
        top: tall ? "20%" : "22%", height: tall ? "20%" : "20%",
        padding: "0 80px",
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", textAlign: "center", zIndex: 4,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 28, marginBottom: 18 }}>
          <span style={{
            height: 1, width: 140,
            background: "linear-gradient(90deg, transparent, #a87528, transparent)",
          }} />
          <div style={{
            width: sizes.glyphBig, height: sizes.glyphBig,
            color: "#a87528",
            display: "flex", alignItems: "center", justifyContent: "center",
            filter: "drop-shadow(0 4px 16px rgba(120,80,30,0.35))",
          }}>
            {React.isValidElement(subject.glyph)
              ? React.cloneElement(subject.glyph, { size: sizes.glyphBig })
              : subject.glyph}
          </div>
          <span style={{
            height: 1, width: 140,
            background: "linear-gradient(90deg, transparent, #a87528, transparent)",
          }} />
        </div>
        <div style={{
          fontFamily: '"Cinzel", serif', fontWeight: 600,
          letterSpacing: ".34em", color: "#6a4514", fontSize: sizes.tag,
          margin: "0 0 14px",
        }}>
          {subject.tag}
        </div>
        <h2 style={{
          fontFamily: '"Cormorant Garamond", serif', fontWeight: 500,
          fontSize: sizes.name, lineHeight: 1, margin: "0 0 16px",
          color: "#2d1a0a", letterSpacing: "-0.01em",
        }}>
          {subject.name}
        </h2>
        <p style={{
          fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic",
          fontWeight: 400, fontSize: sizes.essence, lineHeight: 1.3,
          margin: 0, maxWidth: "88%", color: "#4a3220",
        }}>
          {subject.essence}
        </p>
      </div>

      {/* KEY INSIGHT MEDALLION — labeled "— INSIGHT —" not "— COR•RELATION —" */}
      <div style={{
        position: "absolute", top: tall ? "44%" : "46%", left: 0, right: 0,
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 7,
      }}>
        <div style={{
          position: "relative",
          width: sizes.medSize, height: sizes.medSize, borderRadius: "50%",
          background: "radial-gradient(circle at 32% 28%, #f6dba0 0%, #c89947 28%, #6e4012 68%, #2a1605 100%)",
          boxShadow: [
            "0 0 0 4px rgba(120,80,30,.6)",
            "0 0 0 14px rgba(245,233,207,.96)",
            "0 0 0 18px rgba(120,80,30,.55)",
            "0 0 0 22px rgba(245,233,207,.55)",
            "0 0 70px rgba(255,200,120,.4)",
            "0 30px 70px rgba(40,20,5,.55)",
            "inset 0 -24px 50px rgba(40,20,5,.62)",
            "inset 0 24px 50px rgba(255,235,180,.45)",
          ].join(", "),
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            position: "absolute", inset: 28, borderRadius: "50%",
            backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200' fill='none' stroke='%23f5e0a8' stroke-width='0.6' opacity='0.55'><circle cx='100' cy='100' r='80'/><circle cx='100' cy='100' r='60'/><polygon points='100,30 161,135 39,135'/><polygon points='100,170 161,65 39,65'/></svg>\")",
            backgroundSize: "cover",
          }} />
          <div style={{
            position: "absolute", inset: 36, borderRadius: "50%",
            background: "radial-gradient(circle at 50% 50%, rgba(20,10,4,0.55) 0%, rgba(20,10,4,0.32) 50%, transparent 75%)",
          }} />
          <div style={{
            position: "relative", zIndex: 2, textAlign: "center",
            padding: `0 ${Math.round(sizes.medSize * 0.09)}px`,
            color: "#fff7df",
            textShadow: "0 2px 0 rgba(20,10,4,.95), 0 0 24px rgba(20,10,4,.8), 0 0 4px rgba(20,10,4,.95)",
          }}>
            <div style={{
              fontFamily: '"Cinzel", serif', fontWeight: 700, fontSize: sizes.medLabel,
              letterSpacing: ".36em", color: "#ffe28a",
              marginBottom: Math.round(sizes.medLabel * 0.95),
              textShadow: "0 1px 0 rgba(20,10,4,.9)",
            }}>
              — INSIGHT —
            </div>
            <div style={{
              fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic",
              fontWeight: 600, fontSize: sizes.medTruth, lineHeight: 1.2,
              color: "#fff8e3",
            }}>
              {`\u201C${keyInsight}\u201D`}
            </div>
          </div>
        </div>
      </div>

      {/* DEEPER RESONANCE — 5 punchy truths. Extends to just above the footer
          now that the QR has moved to the top-left corner. */}
      <div style={{
        position: "absolute", left: 0, right: 0,
        top: tall ? "67%" : "70%", bottom: 130,
        padding: "0 60px", zIndex: 4,
        display: "flex", flexDirection: "column",
      }}>
        <div style={{
          fontFamily: '"Cinzel", serif', fontWeight: 700, fontSize: sizes.label,
          letterSpacing: ".36em", color: "#6a4514", textAlign: "center",
          margin: `0 0 ${tall ? 22 : 14}px`,
        }}>
          — THE DEEPER RESONANCE —
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: sizes.truthGap }}>
          {truths.map((tr, i) => (
            <div key={`truth-${i}-${String(tr).slice(0,24)}`} style={{
              display: "flex", gap: 18, alignItems: "flex-start",
              fontFamily: '"Cormorant Garamond", serif', fontSize: sizes.truth,
              fontWeight: 500,
              lineHeight: 1.34, color: "#1c0f04",
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#a87528" strokeWidth="2.4"
                style={{
                  flexShrink: 0, marginTop: Math.round(sizes.truth * 0.34),
                  width: sizes.arrow, height: sizes.arrow,
                  filter: "drop-shadow(0 0 4px rgba(168,117,40,.4))",
                }}
              >
                <path d="M2 12 L20 12 M14 6 L20 12 L14 18" />
              </svg>
              <span>{renderInlineLight(tr)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* QR moved to top-left corner of the card (see above) so the bullets
          can use the full bottom band without overlap. */}

      <Footer />
    </div>
  );
}

// Inline markdown variant — same as renderInline but tuned for the cream
// parchment background (so bold/italic colors lift against light, not dark).
// Returns an array of React nodes to avoid dangerouslySetInnerHTML.
function renderInlineLight(s) {
  return renderInlineTokens(String(s), {
    bold: { color: "#000000", fontWeight: 700 },
    italic: { color: "#6a4514", fontWeight: 500 },
  });
}

// Minimal inline markdown: **bold** → <b>, *italic* → <i>. Returns React
// nodes (no dangerouslySetInnerHTML) so React handles all escaping natively.
function renderInline(s) {
  return renderInlineTokens(String(s), {
    bold: { color: "#ffffff", fontWeight: 700 },
    italic: { color: "#ffd580", fontWeight: 500 },
  });
}

// Shared tokeniser — walks the string once, emits <b>/<i>/text nodes.
// React itself escapes all text content, so no separate escape step is needed.
function renderInlineTokens(s, styles) {
  const out = [];
  const re = /\*\*([^*]+?)\*\*|\*([^*]+?)\*/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push(s.slice(last, m.index));
    if (m[1] !== undefined) {
      out.push(<b key={`b${k++}`} style={styles.bold}>{m[1]}</b>);
    } else {
      out.push(<i key={`i${k++}`} style={styles.italic}>{m[2]}</i>);
    }
    last = re.lastIndex;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}
